<template>
  <div ref="container-ref" class="filter-container">
    <div
      ref="list-ref"
      class="ticket-option-list"
      @scroll="scrollHandler"
    >
      <div
        v-for="item in filterOptions"
        :key="item"
        class="filter-option"
        :class="{ selected: item === currentFocusOption }"
        @click="selectOptionHandler(item)"
      >
        {{ item }}
      </div>
    </div>
    <div v-if="showScroll && !isMobile" class="flex gap-2">
      <NuxtIcon
        name="material-symbols:chevron-left"
        class="cursor-pointer text-6"
        :class="{ disabled: !canScrollLeft }"
        @click="scrollLeft"
      ></NuxtIcon>
      <NuxtIcon
        name="material-symbols:chevron-right"
        class="cursor-pointer text-6"
        :class="{ disabled: !canScrollRight }"
        @click="scrollRight"
      ></NuxtIcon>
    </div>
    <div v-if="isMobile && canScrollRight" class="list-shadow"></div>
  </div>
</template>

<script lang="ts" setup>
const containerRef = useTemplateRef('container-ref')
const listRef = useTemplateRef('list-ref')
const showScroll = ref(false)
const canScrollLeft = ref(false)
const canScrollRight = ref(true)
const isMobile = useMobileByScreen()
const {
  formattedProducts,
  currentFocusOption,
  topHeight,
  isScrollByUser,
} = useAttractionDetail()

const filterOptions = computed(() => {
  return formattedProducts.value.map(item => item.groupName || '')
})

function selectOptionHandler(item: string) {
  const scrollContainer = document.getElementById('scroll-container')
  const auchor = document.getElementById(item)
  const scrollY = scrollContainer?.scrollTop || 0
  const auchorY = auchor?.offsetTop || 0
  scrollContainer?.scrollBy({
    top: auchorY - scrollY - topHeight.value,
    behavior: 'smooth',
  })
  isScrollByUser.value = false
  currentFocusOption.value = item
}

function getListWidth() {
  return listRef.value?.scrollWidth || 0
}

function getListScrollLeft() {
  return listRef.value?.scrollLeft || 0
}

function getContainerWidth() {
  return containerRef.value?.clientWidth || 0
}

function resizeHandler() {
  calcCanScroll()
  showScroll.value = getListWidth() > getContainerWidth()
}

const debounceResizeHandler = debounce(resizeHandler, 100)

function calcCanScroll() {
  canScrollLeft.value = getListScrollLeft() > 0
  canScrollRight.value = getListScrollLeft() + getContainerWidth() < getListWidth()
}

const scrollHandler = debounce(calcCanScroll, 100)

function scrollLeft() {
  if (!canScrollLeft.value) {
    return
  }
  listRef.value?.scrollBy({
    left: -200,
    behavior: 'smooth',
  })
}

function scrollRight() {
  if (!canScrollRight.value) {
    return
  }
  listRef.value?.scrollBy({
    left: 200,
    behavior: 'smooth',
  })
}

watch(() => currentFocusOption.value, async () => {
  await nextTick()
  const el = document.querySelector('.filter-option.selected') as HTMLDivElement
  if (el) {
    listRef.value?.scrollTo({
      left: el.offsetLeft - 10 - 1 / 2 * window.innerWidth + el.offsetWidth / 2,
      behavior: 'smooth',
    })
  }
})

onMounted(() => {
  window.addEventListener('resize', debounceResizeHandler)
  resizeHandler()
})

onUnmounted(() => {
  window.removeEventListener('resize', debounceResizeHandler)
})
</script>

<style lang="scss" scoped>
.filter-container {
  position: sticky;
  padding: 10px 0;
  background-color: #fff;
  top: 0;
  display: flex;
  align-items: center;
  gap: 15px;
  width: 100%;
  z-index: 111;
  .disabled {
    color: #d9d9d9;
  }
  .list-shadow {
    position: absolute;
    right: 0;
    height: 100%;
    width: 100px;
    background-color: red;
    background: linear-gradient(to right, rgba(255, 255, 255, 0), #ffffff);
    pointer-events: none;
  }
}
.ticket-option-list {
  width: 100%;
  overflow-x: auto;
  display: flex;
  gap: 8px;
  padding: 1px 0;
  .filter-option {
    color: #121212;
    display: flex;
    align-items: center;
    max-width: 200px;
    padding: 14px 20px;
    border-radius: 10px;
    border: 1px solid #00000014;
    font-size: 16px;
    font-weight: 400;
    line-height: 20px;
    flex-shrink: 0;
    cursor: pointer;
    user-select: none;

    &:hover {
      background-color: #f6f6f6;
    }

    &.selected {
      background-color: var(--el-color-primary-light-9);
      color: var(--tc-color-primary);
      border: 2px solid var(--tc-color-primary);
      padding: 13px 19px;
      font-weight: 600;
    }
  }
}
</style>

<style>
@media screen and (max-width: 768px) {
  .filter-container {
    .ticket-option-list {
      .filter-option {
        color: #121212;
        font-size: 12px;
        line-height: 15px;
        padding: 10px 15px;
        &.selected {
          font-weight: 700;
          padding: 9px 14px;
        }
      }
    }
  }
}
</style>
